import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from './Button';

/**
 * Estado vacio.
 *
 * La primera instalacion del Ecosystem no tiene ninguna aplicacion y aun asi
 * la interfaz tiene que verse terminada: por eso los vacios son un componente
 * de primera clase y no un `if` improvisado.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-card border border-dashed border-border',
        'bg-surface/40 px-6 py-14 text-center',
        className,
      )}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-surface-secondary text-text-muted">
        {icon ?? <Inbox className="h-6 w-6" aria-hidden />}
      </div>
      <h3 className="text-base font-semibold text-text-primary">{title}</h3>
      {description ? (
        <p className="mt-1.5 max-w-md text-sm text-text-muted">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = 'No se pudo cargar la informacion',
  description,
  requestId,
  onRetry,
  className,
}: {
  title?: string;
  description?: ReactNode;
  requestId?: string | null;
  onRetry?: () => void;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-card border border-danger/30',
        'bg-danger-soft px-6 py-12 text-center',
        className,
      )}
    >
      <AlertTriangle className="mb-3 h-7 w-7 text-danger" aria-hidden />
      <h3 className="text-base font-semibold text-text-primary">{title}</h3>
      {description ? (
        <p className="mt-1.5 max-w-md text-sm text-text-secondary">{description}</p>
      ) : null}
      {requestId ? (
        <p className="mt-2 font-mono text-xs text-text-muted">ID de peticion: {requestId}</p>
      ) : null}
      {onRetry ? (
        <Button
          variant="secondary"
          size="sm"
          className="mt-5"
          onClick={onRetry}
          leftIcon={<RefreshCw className="h-4 w-4" aria-hidden />}
        >
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn('animate-pulse rounded-lg bg-surface-secondary', className)}
      aria-hidden
    />
  );
}

export function SkeletonList({ rows = 4 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 3 }: { count?: number }): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-44 w-full" />
      ))}
    </div>
  );
}
